// src/components/LoginModal.jsx

import React, { useState } from 'react';
import { X, Eye, EyeOff, KeyRound, ArrowLeft, Mail } from 'lucide-react'; // Added Icons
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth'; // Added Reset Function
import { auth } from '../config/firebase-config';

const LoginModal = ({ isOpen, onClose, onSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    
    // UI State
    const [showPassword, setShowPassword] = useState(false);
    const [isResetMode, setIsResetMode] = useState(false); // Toggles Login vs Reset View
    const [loading, setLoading] = useState(false);
    
    // Feedback State
    const [message, setMessage] = useState(''); 
    const [messageType, setMessageType] = useState(''); // 'error' or 'success'

    if (!isOpen) return null;

    // Helper to clear state when switching views
    const switchMode = (mode) => {
        setIsResetMode(mode);
        setMessage('');
        setMessageType('');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            if (onSuccess) onSuccess(); 
            onClose(); 
        } catch (err) {
            let friendlyMessage = "An unexpected error occurred. Please try again.";
            
            // Translate Firebase error codes to human-readable text
            if (err.code === 'auth/invalid-credential') {
                friendlyMessage = "Incorrect email or password. Please try again.";
            } else if (err.code === 'auth/too-many-requests') {
                friendlyMessage = "Too many failed attempts. Please reset your password or try again later.";
            } else if (err.code === 'auth/user-disabled') {
                friendlyMessage = "This account has been disabled by an administrator.";
            }

            setMessage(friendlyMessage);
            setMessageType('error');
        } finally {
            setLoading(false); 
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (!email) {
            setMessage("Please enter your email address first.");
            setMessageType('error');
            return;
        }
        
        setMessage('');
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage(`Reset link sent to ${email}. Check your inbox!`);
            setMessageType('success');
        } catch (err) {
            setMessage("Reset Failed: " + err.message);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                
                {/* HEADER */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        {isResetMode ? <KeyRound size={24} className="text-brand-primary"/> : "System Access"}
                        {isResetMode && "Reset Password"}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={24} />
                    </button>
                </div>

                {/* FEEDBACK MESSAGE */}
                {message && (
                    <div className={`mb-4 p-3 border text-sm font-bold rounded-lg text-center ${messageType === 'success' ? 'bg-green-50 border-green-100 text-green-600' : 'bg-red-50 border-red-100 text-red-600'}`}>
                        {message}
                    </div>
                )}

                {/* === VIEW 1: LOGIN FORM === */}
                {!isResetMode && (
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                            <div className="relative">
                                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input 
                                    name="email" type="email" 
                                    className="w-full p-3 pl-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary outline-none transition-all" 
                                    placeholder="user@example.com" 
                                    value={email} onChange={(e) => setEmail(e.target.value)} required 
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                            <div className="relative">
                                <input 
                                    name="password" 
                                    type={showPassword ? "text" : "password"} // <--- TOGGLE TYPE
                                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary outline-none transition-all pr-10" 
                                    placeholder="••••••••" 
                                    value={password} onChange={(e) => setPassword(e.target.value)} required 
                                />
                                <button 
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            <div className="flex justify-end mt-1">
                                <button 
                                    type="button" 
                                    onClick={() => switchMode(true)}
                                    className="text-xs font-bold text-brand-primary hover:underline"
                                >
                                    Forgot Password?
                                </button>
                            </div>
                        </div>
                        <button 
                            type="submit" disabled={loading}
                            className="w-full py-3 bg-brand-primary text-white rounded-lg font-bold hover:opacity-90 transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Authenticating...' : 'Login to Portal'}
                        </button>
                    </form>
                )}

                {/* === VIEW 2: PASSWORD RESET FORM === */}
                {isResetMode && (
                    <form onSubmit={handlePasswordReset} className="space-y-4">
                        <p className="text-sm text-slate-500 mb-4">
                            Enter your email address and we'll send you a link to reset your password.
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                            <div className="relative">
                                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input 
                                    name="email" type="email" 
                                    className="w-full p-3 pl-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary outline-none transition-all" 
                                    placeholder="user@example.com" 
                                    value={email} onChange={(e) => setEmail(e.target.value)} required 
                                />
                            </div>
                        </div>
                        
                        <button 
                            type="submit" disabled={loading}
                            className="w-full py-3 bg-slate-900 text-white rounded-lg font-bold hover:bg-black transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
                        >
                            {loading ? 'Sending...' : 'Send Reset Link'}
                        </button>

                        <button 
                            type="button" 
                            onClick={() => switchMode(false)}
                            className="w-full py-3 text-slate-500 font-bold text-sm hover:text-slate-800 transition-colors flex justify-center items-center gap-2"
                        >
                            <ArrowLeft size={16} /> Back to Login
                        </button>
                    </form>
                )}

            </div>
        </div>
    );
};

export default LoginModal;